// Pure functions for the Multi-year Analytics view. No React, no Supabase.
// Spec: docs/superpowers/specs/2026-05-04-multi-year-analytics-design.md

export const MONTH_KEYS = [
  'jan', 'feb', 'mar', 'apr', 'may', 'jun',
  'jul', 'aug', 'sep', 'oct', 'nov', 'dec',
];

/**
 * Given a property's season_start_md / season_end_md (both 'MM-DD'),
 * return the ordered array of 3-letter lowercase month keys the season covers.
 *
 * Cross-year seasons (start month-day > end month-day) are NOT supported
 * by the multi-year view and throw — multi-year aggregation across calendar
 * boundaries is out of scope per the design spec.
 */
export function monthsInSeasonWindow(startMd, endMd) {
  if (!startMd || !endMd) {
    throw new Error('monthsInSeasonWindow requires startMd and endMd');
  }
  if (startMd > endMd) {
    throw new Error(
      'monthsInSeasonWindow does not support cross-year seasons (startMd > endMd)',
    );
  }
  const startMonthIdx = Number(startMd.slice(0, 2)) - 1;
  const endMonthIdx   = Number(endMd.slice(0, 2)) - 1;
  return MONTH_KEYS.slice(startMonthIdx, endMonthIdx + 1);
}

// ─── Source-mix helper ───────────────────────────────────────────────────────

function classifySource(src) {
  if (src === 'Direct') return 'direct';
  if (src === 'Booking.com') return 'booking_com';
  return 'other';
}

function emptySourceMix() {
  return { direct: 0, booking_com: 0, other: 0 };
}

function addSourceMix(a, b) {
  if (!a && !b) return null;
  if (!a) return { ...b };
  if (!b) return { ...a };
  return {
    direct:      (a.direct || 0)      + (b.direct || 0),
    booking_com: (a.booking_com || 0) + (b.booking_com || 0),
    other:       (a.other || 0)       + (b.other || 0),
  };
}

// ─── Booking → per-month aggregation ─────────────────────────────────────────

function nightsBetween(ci, co) {
  const a = new Date(ci + 'T12:00:00Z');
  const b = new Date(co + 'T12:00:00Z');
  return Math.round((b - a) / 86400000);
}

function monthKeyOfIsoDate(iso) {
  const m = Number(iso.slice(5, 7));
  return MONTH_KEYS[m - 1];
}

/**
 * For a single year's confirmed bookings, return:
 *   {
 *     monthly: { jul: { revenue, nights, sourceMix }, ... },
 *     totalRevenue, totalNights, sourceMix
 *   }
 *
 * Bookings are aggregated by their check_in month — a stay that crosses
 * a month boundary is fully attributed to its check-in month. This matches
 * how the existing Season-view source mix per booking is computed.
 */
function aggregateRealBookings(bookings, year) {
  const monthly = {};
  let totalRevenue = 0;
  let totalNights = 0;
  let sourceMix = null;

  for (const b of bookings) {
    if (b.status !== 'confirmed') continue;
    if (b.check_in.slice(0, 4) !== String(year)) continue;
    const nights = nightsBetween(b.check_in, b.check_out);
    if (nights <= 0) continue;
    const price = Number(b.price) || 0;
    const monthKey = monthKeyOfIsoDate(b.check_in);
    const cls = classifySource(b.source);

    if (!monthly[monthKey]) {
      monthly[monthKey] = { revenue: 0, nights: 0 };
    }
    monthly[monthKey].revenue += price;
    monthly[monthKey].nights  += nights;

    totalRevenue += price;
    totalNights  += nights;
    if (!sourceMix) sourceMix = emptySourceMix();
    sourceMix[cls] += price;
  }

  return { monthly, totalRevenue, totalNights, sourceMix };
}

// ─── mergeYearData ───────────────────────────────────────────────────────────

/**
 * Merge real bookings + optional past_seasons row for one year into a
 * unified per-year record.
 *
 * Inputs:
 *   year          - calendar year (number)
 *   bookings      - bookings array pre-filtered by the caller to year Y's season window
 *                   (caller is responsible for filtering; this function only re-filters by year)
 *   pastSeason    - past_seasons row for this year, or null
 *   seasonWindow  - { startMd, endMd } — passed for API consistency; caller pre-filters bookings
 *   isInProgress  - true for the current in-progress season
 *
 * Output:
 *   {
 *     year, totalRevenue, totalNights | null,
 *     monthly: { <monthKey>: { revenue, nights | null, fromBackfill } },
 *     sourceMix: { direct, booking_com, other } | null,
 *     hasRealBookings, hasBackfill, isInProgress,
 *     warning?: 'season-total-collides-with-bookings'
 *   }
 */
export function mergeYearData({ year, bookings, pastSeason, seasonWindow, isInProgress }) {
  const real = aggregateRealBookings(bookings || [], year);
  const hasRealBookings = real.totalRevenue > 0 || real.totalNights > 0;
  const hasBackfill = !!pastSeason;

  // Case: no backfill — pure real-bookings result
  if (!hasBackfill) {
    const monthly = {};
    for (const [k, v] of Object.entries(real.monthly)) {
      monthly[k] = { revenue: v.revenue, nights: v.nights, fromBackfill: false };
    }
    return {
      year,
      totalRevenue: real.totalRevenue,
      totalNights:  hasRealBookings ? real.totalNights : 0,
      monthly,
      sourceMix:    real.sourceMix,
      hasRealBookings,
      hasBackfill:  false,
      isInProgress: !!isInProgress,
    };
  }

  const backfillMonths = pastSeason.months || null;
  const hasBackfillMonthly = backfillMonths && Object.keys(backfillMonths).length > 0;

  // Defensive: season-total-only backfill colliding with real bookings
  if (hasRealBookings && !hasBackfillMonthly) {
    const monthly = {};
    for (const [k, v] of Object.entries(real.monthly)) {
      monthly[k] = { revenue: v.revenue, nights: v.nights, fromBackfill: false };
    }
    return {
      year,
      totalRevenue: real.totalRevenue,
      totalNights:  real.totalNights,
      monthly,
      sourceMix:    real.sourceMix,
      hasRealBookings: true,
      hasBackfill:  true,
      isInProgress: !!isInProgress,
      warning: 'season-total-collides-with-bookings',
    };
  }

  // Case: backfill only — no real bookings
  if (!hasRealBookings) {
    if (hasBackfillMonthly) {
      const monthly = {};
      let totalRevenue = 0;
      let totalNights  = 0;
      let nightsKnown = false;
      for (const [k, v] of Object.entries(backfillMonths)) {
        const rev = Number(v?.revenue) || 0;
        const nts = (v && v.nights != null) ? Number(v.nights) : null;
        monthly[k] = { revenue: rev, nights: nts, fromBackfill: true };
        totalRevenue += rev;
        if (nts != null) {
          totalNights += nts;
          nightsKnown = true;
        }
      }
      return {
        year,
        totalRevenue,
        totalNights:  nightsKnown ? totalNights : null,
        monthly,
        sourceMix:    pastSeason.source_split || null,
        hasRealBookings: false,
        hasBackfill:  true,
        isInProgress: !!isInProgress,
      };
    }
    // Season-total-only path
    return {
      year,
      totalRevenue: Number(pastSeason.total_revenue) || 0,
      totalNights:  pastSeason.total_nights != null ? Number(pastSeason.total_nights) : null,
      monthly:      {},
      sourceMix:    pastSeason.source_split || null,
      hasRealBookings: false,
      hasBackfill:  true,
      isInProgress: !!isInProgress,
    };
  }

  // Case: hybrid — merge at month level. Real wins per month; backfill fills gaps.
  const monthly = {};
  for (const [k, v] of Object.entries(real.monthly)) {
    monthly[k] = { revenue: v.revenue, nights: v.nights, fromBackfill: false };
  }
  for (const [k, v] of Object.entries(backfillMonths)) {
    if (monthly[k]) continue; // real wins
    const rev = Number(v?.revenue) || 0;
    const nts = (v && v.nights != null) ? Number(v.nights) : null;
    monthly[k] = { revenue: rev, nights: nts, fromBackfill: true };
  }

  let totalRevenue = 0;
  let totalNights  = 0;
  let nightsKnown = true;
  for (const v of Object.values(monthly)) {
    totalRevenue += v.revenue;
    if (v.nights == null) {
      nightsKnown = false;
    } else {
      totalNights += v.nights;
    }
  }

  // Source mix: real bookings + backfill source_split (proportional allocation
  // is overkill — sum what we know).
  const sourceMix = addSourceMix(real.sourceMix, pastSeason.source_split || null);

  return {
    year,
    totalRevenue,
    totalNights:  nightsKnown ? totalNights : null,
    monthly,
    sourceMix,
    hasRealBookings: true,
    hasBackfill: true,
    isInProgress: !!isInProgress,
  };
}

// ─── buildYearRange ──────────────────────────────────────────────────────────

/**
 * Ascending, contiguous list of calendar years to display: from the earliest
 * data year (or an explicitly added earlier year) up to currentYear, inclusive.
 *
 *   dataYears         - years that have data (real season-window bookings or a
 *                       past_seasons row). Non-integer / NaN entries are ignored.
 *   extraEarliestYear - an explicitly "added earlier" year, or null.
 *
 * The upper bound is always currentYear: neither dataYears nor extraEarliestYear
 * can extend the range forward past it.
 */
export function buildYearRange({ currentYear, dataYears = [], extraEarliestYear = null }) {
  const candidates = [currentYear, ...dataYears.filter(y => Number.isInteger(y))];
  if (Number.isInteger(extraEarliestYear)) candidates.push(extraEarliestYear);
  const earliest = Math.min(...candidates);
  const out = [];
  for (let y = earliest; y <= currentYear; y++) out.push(y);
  return out;
}

// ─── yoyDeltaPct ───────────────────────────────────────────────────────────

/**
 * Percentage change prior → current. Single source of truth for every YoY
 * figure (yearly bars and grid cells); the component applies the display
 * gating (in-progress / complete-month / earliest-year).
 *
 * Returns null when there is no meaningful comparison: current is null/undefined,
 * prior is null/undefined, or prior <= 0. A current of 0 against a positive
 * prior is a real -100 (a genuine drop, shown — not suppressed).
 */
export function yoyDeltaPct(current, prior) {
  if (current == null || prior == null) return null;
  if (prior <= 0) return null;
  return ((current - prior) / prior) * 100;
}
